import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import actions from 'roles-actions-permissions/actions';
import usePermissions from 'hooks/permissions-hooks';
import Button from '@material-ui/core/Button';
import axiosClient from 'http/axios-client';
import { useAlerts } from 'hooks/alerts-hooks';
import * as _ from 'lodash';
import MonthSelector from 'components/basic/MonthSelector';
import { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import * as moment from 'moment';
import { SearchAndFilterButtons } from 'components/basic/SearchAndFilterButtons';
import { approvalApply } from 'utils/reportsUtils';
import { AiOutlineLineChart } from 'react-icons/ai';
import HasAnswersHook from 'hooks/hasAnswers-hook';
import { getFullRegistrationStatus } from '../consents/ConsentsTable';
import { roles } from '../../../cassandra-shared-js/roles';
import { filterTypes } from '../../basic/AdvancedFilters';
import AppTable from '../../basic/AppTable';
import AppLoader from '../../basic/AppLoader';
import { useItemsReports } from '../../../hooks/atoms/items-hook-reports';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import getTheme from '../../../theme/theme';
import CollectionItemHeader from '../../layout/CollectionItemHeader';

export const parseDate = (dateString) => {
  if (dateString == null) { return ''; }
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const formattedDay = 10 > day ? `0${ day }` : day;
  const formattedMonth = 10 > month ? `0${ month }` : month;

  return `${ formattedDay }/${ formattedMonth }/${ year }`;
};

const downloadData = async (userHasRole, setDownloadAllLoading, filterQuery) => {
  setDownloadAllLoading(true);
  const theKits = await axiosClient.get(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    params: {
      ...filterQuery,
    },
  });

  if (null == theKits) {
    setDownloadAllLoading(false);
    return;
  }

  setDownloadAllLoading(false);

  const getRealStatus = (status) => {
    if (false === status) {
      return 'false';
    }
    if (true === status) {
      return 'true';
    }
    return 'pending';
  };

  const data = theKits.map((kit) => {
    const obj = {
      'Serial Number':
        null !== kit.serial_number_id.ancestor ? `${ kit.serial_number_id.serial?.split('_')[0] } Repeat(${ parseDate(kit.created_at) })`
          : kit.serial_number_id?.serial || '',
      'Report delivery date': parseDate(kit.report_delivery_date) ?? '',
      'Thematic Package': kit.thematic_package?.thematic_category_id?.name ?? '',
      'Report language': kit.locale ?? '',
      // TODO: You should show this..
      // 'Questionnaire Availability': `${ hasQuestionnaireObj[kit.serial_number_id.serial] }` ?? 'false',
      'Genotype Data Available': `${ true === kit?.health_data_available }`,
      'Polygenic Risk Available': `${ true === kit?.polygenic_risk_available }`,
      'PDF Available': `${ true === kit?.pdf_available }`,
      'Dynamic Report Available': `${ true === kit?.dynamic_report_available }`,
      'Bioinformatic Status': getRealStatus(kit?.analysis_approval),
      'Nutrition Genetics': getRealStatus(kit?.nutrition_geneticist_approval),
      'Registration Status': getFullRegistrationStatus(kit),
    };

    if (!userHasRole(roles.ADMINISTRATOR_ROLE)) {
      delete obj.locale;
    }

    if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
      delete obj['Questionnaire Availability'];
      delete obj['Genotype Data Available'];
      delete obj['Polygenic Risk Available'];
      delete obj['PDF Available'];
      delete obj['Dynamic Report Available'];
      delete obj['Medical Status'];
      delete obj['Registration Status'];
    } else if (userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)) {
      delete obj['Questionnaire Availability'];
      delete obj['Genotype Data Available'];
      delete obj['Polygenic Risk Available'];
      delete obj['PDF Available'];
      delete obj['Dynamic Report Available'];
      delete obj['Medical Status'];
      delete obj['Registration Status'];
    } else if (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) {
      delete obj['Thematic Package'];
      delete obj['Questionnaire Availability'];
      delete obj['Genotype Data Available'];
      delete obj['Polygenic Risk Available'];
      delete obj['PDF Available'];
      delete obj['Dynamic Report Available'];
      delete obj['Bioinformatic Status'];
      delete obj['Registration Status'];
    }
    return obj;
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet['!cols'] = fitToColumn(data);

  function fitToColumn(arrayOfArray) {
    if (!arrayOfArray[0]) { // If file is empty
      return null;
    }
    return Object.keys(arrayOfArray[0]).map((key) => {
      return { wch: key.length };
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, 'Nutrigenetix Health Reports.xlsx');
};

function multipleApprove(client, selected, url, field, kits, setShowLoader, refetch, setSelected, allKitsRefetch, value = true) {
  const toBePosted = [];

  if (null == kits) {
    return;
  }

  selected.forEach((id) => {
    const kit = kits.find((theKit) => {
      return theKit.id === id;
    });

    if (kit === undefined || 'done' === kit.registrationStatus || true !== kit.pdf_available) { return; }

    if ('analysis_approval' === field && value === kit.analysis_approval) {
      return;
    }

    if ('nutrition_geneticist_approval' === field && (true !== kit.analysis_approval || value === kit.nutrition_geneticist_approval)) {
      return;
    }

    if ('cardiologist_approval' === field && ((true !== kit.nutrition_geneticist_approval && kit.serial_number_id.ancestor == null) || value === kit.cardiologist_approval)) {
      return;
    }

    toBePosted.push(kit.serial_number_id.serial);
  });

  if (0 === toBePosted.length) {
    return;
  }

  setShowLoader(true);

  if ('cardiologist_approval' === field) {
    const promises = toBePosted.map((serial) => {
      return client.put(`${ url }/${ serial }`, {
        [field]: value,
      });
    });

    Promise.all([ ...promises ])
      .finally(() => {
        Promise.all([ refetch(), allKitsRefetch() ]).finally(() => {
          setSelected([]);
          setShowLoader(false);
        });
      });
  } else {
    const requestBody = toBePosted.map(((serial) => {
      return {
        serial,
        [field]: value,
      };
    }));

    client.put(url, requestBody)
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        Promise.all([ refetch(), allKitsRefetch() ])
          .finally(() => {
            setSelected([]);
            setShowLoader(false);
          });
      });
  }
}

const RestartProcess = ({ serial, url, setShowLoader, refetch, enabled, method, setSelected, disabledButton }) => {
  const { alertError } = useAlerts();

  return (
    <div style={ { display: 'flex', justifyContent: 'center' } }>
      {
        enabled && (
          <Button
            variant='outlined'
            disabled={ disabledButton }
            color='secondary'
            onClick={ () => {
              setShowLoader(true);
              axiosClient[method](`${ url }/${ serial }`)
                .catch((error) => {
                  if (error.response.data?.data?.code) {
                    if ('questionnaireNotExists' === error.response.data.data.code) {
                      alertError('Questionnaire doesn\'t exist.');
                    } else if ('registrationKitNotExist' === error.response.data.data.code) {
                      alertError('Registration Kit doesn\t exist.');
                    } else if ('serialNumberNotExist' === error.response.data.data.code) {
                      alertError('Serial number doesn\t exist.');
                    } else if ('allPRSAreUpdated' === error.response.data.data.code) {
                      alertError('All PRS are updated.');
                    } else {
                      console.log('not found', error.response.data.data);
                    }
                  } else {
                    console.log('no data', error);
                  }
                })
                .finally(() => {
                  refetch().then(() => {
                    setSelected([]);
                    setShowLoader(false);
                  });
                });
            } }
          >
            Restart process
          </Button>
        )
      }
    </div>
  );
};

const NgReportsTable = () => {
  const [ selected, setSelected ] = useState([]);
  const [ filterQuery, setFilterQuery ] = useState({ });
  const [ loading, setLoading ] = useState(false);
  const [ selectedMonths, setSelectedMonths ] = useState(1);

  const handleChange = (event) => { setSelectedMonths(event.target.value); };

  const startDate = moment().subtract(selectedMonths, 'months').startOf('day').toISOString();

  const [ downloadAllLoading, setDownloadAllLoading ] = useState(false);
  const [ downloadLoading, setDownloadLoading ] = useState(false);

  let kitsFilterQuery = {
    ...filterQuery,
    _where: [
      { _or:
        filterQuery?._where?._or },
      { _or: [
        [{ 'thematic_package.thematic_category_id.name_eq': 'NutriGenetix' }],
        [{ 'thematic_package.thematic_category_id.name_eq': 'NutriGenetix-Laboratories' }],
      ] },

      { _or: [
        [{ 'serial_number_id.report_payment_status_eq': 'paid' }],
        [{ 'serial_number_id.report_payment_status_eq': 'free' }],
      ] },
    ],
  };

  let allKitsFilterQuery = {
    ...filterQuery,
    _limit: -1,
    _where: [
      { _or:
        filterQuery?._where?._or },
      { _or: [
        [{ 'thematic_package.thematic_category_id.name_eq': 'NutriGenetix' }],
        [{ 'thematic_package.thematic_category_id.name_eq': 'NutriGenetix-Laboratories' }],
      ] },
      { _or: [
        [{ 'serial_number_id.report_payment_status_eq': 'paid' }],
        [{ 'serial_number_id.report_payment_status_eq': 'free' }],
      ] },
    ],
  };

  const { hasPermissions, userHasRole, userHasRoleExact } = usePermissions();

  if (userHasRoleExact(roles.THIRD_PARTY_CARDIOLOGIST)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      pdf_available_eq: true,
      nutrition_geneticist_approval: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      pdf_available_eq: true,
      nutrition_geneticist_approval: true,
      'serial_number_id.ancestor_null': true,
    };
  } else if (hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };
  } else if (userHasRoleExact(roles.NUTRITION_GENETICS_USER)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      pdf_available_eq: true,
      analysis_approval_eq: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      pdf_available_eq: true,
      analysis_approval_eq: true,
      'serial_number_id.ancestor_null': true,
    };
  }

  const {
    items: kits,
    itemsLoading: kitsLoading,
    pagination,
    refetch,
  } = useItemsReports(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    filterQuery: kitsFilterQuery,
  });

  // query for answered questionnaires
  const { hasAnswers, hasAnswersIsLoading } = HasAnswersHook(kits);

  const {
    items: allKits,
    itemsLoading: allKitsLoading,
    refetch: allKitsRefetch,
    countFiltered,
  } = useItemsReports(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    filterQuery: allKitsFilterQuery,
  });

  if (kitsLoading || allKitsLoading) {
    return <AppLoader />;
  }

  let tableColumns = [
    { id: 1,
      label: 'Serial Number',
      field: 'serial_number_id.serial',
      customRenderer({ row }) {
        if (null !== row.serial_number_id.ancestor) {
          return (
            <span>{`${ row.serial_number_id.serial?.split('_')[0] } Repeat(${ parseDate(row.created_at) })`}</span>
          );
        }
        return (
          <span>{row.serial_number_id.serial}</span>
        );
      } },
    { id: 220,
      label: 'Report delivery date',
      field: 'report_delivery_date',
      customRenderer({ row }) {
        return (
          <>
            {parseDate(row.report_delivery_date)}
          </>
        );
      } },
    { id: 2, label: 'Thematic Package', field: 'thematic_package.thematic_category_id.name', noSort: true },
    { id: 3, label: 'Download Report', field: 'pdf_available', downloadReport: true, ngDownload: true },
    ...(userHasRole(roles.ADMINISTRATOR_ROLE)) ? [{
      id: 931,
      label: 'Report language',
      noSort: false,
      field: 'locale',
      kitLang: true,
    }] : [],
    { id: 40,
      label: 'Questionnaire Availability',
      field: 'pdf_available',
      noSort: true,
      customRenderer({ row }) {
        const { serial } = row.serial_number_id;
        return (
          <>
            {!hasAnswersIsLoading && hasAnswers[serial] ? (
              <div style={ { display: 'flex', justifyContent: 'center' } }>
                <CheckIcon
                  style={ { color: '#93C120' } }
                />
              </div>
            ) : (
              <div style={ { display: 'flex', justifyContent: 'center' } }>
                <ClearIcon style={ { color: 'red' } } />
              </div>
            )}
          </>
        );
      } },
    { id: 5, label: 'Genotype Data Available', field: 'health_data_available', doneOrButton: true },
    { id: 6, label: 'Polygenic Risk Available', field: 'polygenic_risk_available', doneOrButton: true },
    { id: 4, label: 'PDF Available', field: 'pdf_available', doneOrButton: true },
    { id: 7, label: 'Dynamic Report Available', field: 'dynamic_report_available', doneOrButton: true },
    { id: 90,
      label: 'Genotype Algorithm',
      field: 'registrationStatus',
      noSort: true,
      customRenderer({
        setShowLoader,
        refetch: theRefetch,
        row,
      }) {
        const enabled = true;

        let disabledButton = false;
        if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) && false !== row.analysis_approval) {
          disabledButton = true;
        }

        const { serial } = row.serial_number_id;

        return (
          <RestartProcess
            enabled={ enabled }
            serial={ serial }
            url='registration-kits/reportProcess'
            method='post'
            setShowLoader={ setShowLoader }
            refetch={ theRefetch }
            setSelected={ setSelected }
            disabledButton={ 'done' === row.registrationStatus || disabledButton }
          />
        );
      } },
  ];

  if (hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT)) {
    tableColumns = [
      { id: 1,
        label: 'Serial Number',
        field: 'serial_number_id.serial',
        customRenderer({ row }) {
          if (null !== row.serial_number_id.ancestor) {
            return (
              <span>{`${ row.serial_number_id.serial?.split('_')[0] } Repeat(${ parseDate(row.created_at) })`}</span>
            );
          }
          return (
            <span>{row.serial_number_id.serial}</span>
          );
        } },
      { id: 3, label: 'Download Report', field: 'pdf_available', downloadReport: true, noSort: true },
    ];
  }

  if (hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_VIEW)) {
    tableColumns.push(
      { id: 9,
        label: 'Bioinformatic Status',
        field: 'analysis_approval',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = false === row.analysis_approval && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.analysis_approval) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
              {value}
            </div>
          );
        } },
      ...hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT)
        ? [{ id: 79,
          label: 'Bioinformatic Approval',
          field: 'analysis_approval',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (true !== row.pdf_available || 'done' === row.registrationStatus) {
              return <div />;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
                { (null === row.analysis_approval || false === row.analysis_approval) && 'done' !== row.registrationStatus && (

                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/analysis-update/${ serial }`, 'analysis_approval', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.analysis_approval && 'done' !== row.registrationStatus && (
                  <Button
                    disabled={ !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    variant='outlined'
                    onClick={ () => { approvalApply(`registration-kits/analysis-update/${ serial }`, serial, 'analysis_approval', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>
                )}
              </div>
            );
          } }] : [],
    );
  }

  if (userHasRole(roles.NUTRITION_GENETICS_USER) || userHasRole(roles.ADMINISTRATOR_ROLE) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
    tableColumns.push(
      { id: 122339,
        label: 'Nutrition Genetics Approval Status',
        field: 'nutrition_geneticist_approval',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = false === row.nutrition_geneticist_approval && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.nutrition_geneticist_approval) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
              {value}
            </div>
          );
        } },
      ...userHasRole(roles.NUTRITION_GENETICS_USER)
        ? [{ id: 132579,
          label: 'Nutrition Genetics Approval',
          field: 'nutrition_geneticist_approval',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (true !== row.pdf_available || 'done' === row.registrationStatus) {
              return <div />;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>

                { (null === row.nutrition_geneticist_approval || false === row.nutrition_geneticist_approval) && 'done' !== row.registrationStatus && (

                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/medical-geneticist-update/${ serial }`, 'nutrition_geneticist_approval', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.nutrition_geneticist_approval && 'done' !== row.registrationStatus && (
                  <Button
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    variant='outlined'
                    onClick={ () => { approvalApply(`registration-kits/medical-geneticist-update/${ serial }`, 'nutrition_geneticist_approval', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>

                )}
              </div>
            );
          } }] : [],
    );
  }

  if (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST) || userHasRole(roles.ADMINISTRATOR_ROLE) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
    tableColumns.push(
      { id: 1239,
        label: 'Chief Scientific Officer Approval Status',
        field: 'cardiologist_approval',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = false === row.cardiologist_approval && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.cardiologist_approval) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
              {value}
            </div>
          );
        } },
      ...userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)
        ? [{ id: 12579,
          label: 'Chief Scientific Officer Approval',
          field: 'cardiologist_approval',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (true !== row.pdf_available || 'done' === row.registrationStatus) {
              return <div />;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
                { (null === row.cardiologist_approval || false === row.cardiologist_approval) && 'done' !== row.registrationStatus && (

                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/cardiologist-update/${ serial }`, 'cardiologist_approval', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.cardiologist_approval && 'done' !== row.registrationStatus && (
                  <Button
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    variant='outlined'
                    onClick={ () => { approvalApply(`registration-kits/cardiologist-update/${ serial }`, 'cardiologist_approval', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>

                )}
              </div>
            );
          } }] : [],
    );
  }

  if (!hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT)
  && !userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)
  && !userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE)
 && !userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)
  ) {
    tableColumns.push(
      { id: 15,
        label: 'Registration Status',
        field: 'registrationStatus',
        noSort: true,
        customRenderer({ row }) {
          return (
            <>
              {
                getFullRegistrationStatus(row)
              }
            </>
          );
        } },
    );
  }

  return (
    <div>
      <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: getTheme().spacing(2) } }>
        <AiOutlineLineChart
          style={ { color: getTheme().palette.secondary.dark, marginRight: getTheme().spacing(2) } }
          size={ 36 }
        />
        <CollectionItemHeader
          title='NutriGenetix Reports'
          subtitle={ `Entries: ${ countFiltered ?? 0 }` }
          actions={ (
            <>
            </>
          ) }
        />
      </div>
      <div style={ { display: 'flex', gap: '5px', marginBottom: '15px' } }>
        <Button
          style={ { maxHeight: '36px' } }
          variant='contained'
          color='secondary'
          onClick={ () => {
            setSelected(allKits?.map((ar) => { return ar.id; }));
          } }
        >
          Select All
        </Button>
        <Button
          style={ { maxHeight: '36px' } }
          variant='contained'
          color='secondary'
          onClick={ () => {
            setSelected([]);
          } }
        >
          Clear Selection
        </Button>
        {
          (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) || userHasRole(roles.NUTRITION_GENETICS_USER) || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) && (
            <Button
              style={ { maxHeight: '36px' } }
              variant='contained'
              color='secondary'
              onClick={ () => {
                if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
                  multipleApprove(axiosClient, selected, 'registration-kits/analysis-batch-update', 'analysis_approval', allKits, setLoading, refetch, setSelected, allKitsRefetch);
                } else if (userHasRoleExact(roles.NUTRITION_GENETICS_USER)) {
                  multipleApprove(axiosClient, selected, 'registration-kits/geneticist-batch-update', 'nutrition_geneticist_approval', allKits, setLoading, refetch, setSelected, allKitsRefetch);
                } else {
                  multipleApprove(axiosClient, selected, 'registration-kits/cardiologist-update', 'cardiologist_approval', allKits, setLoading, refetch, setSelected, allKitsRefetch);
                }
              } }
            >
              Approve selected
            </Button>
          )
        }
        {
          (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) || userHasRole(roles.NUTRITION_GENETICS_USER) || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) && (
            <Button
              style={ { maxHeight: '36px' } }
              variant='contained'
              color='secondary'
              onClick={ () => {
                if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
                  multipleApprove(axiosClient, selected, 'registration-kits/analysis-batch-update', 'analysis_approval', allKits, setLoading, refetch, setSelected, allKitsRefetch, false);
                } else if (userHasRoleExact(roles.NUTRITION_GENETICS_USER)) {
                  multipleApprove(axiosClient, selected, 'registration-kits/geneticist-batch-update', 'nutrition_geneticist_approval', allKits, setLoading, refetch, setSelected, allKitsRefetch, false);
                } else {
                  multipleApprove(axiosClient, selected, 'registration-kits/cardiologist-update', 'cardiologist_approval', allKits, setLoading, refetch, setSelected, allKitsRefetch, false);
                }
              } }
            >
              Disapprove selected
            </Button>
          )
        }

        <LoadingButtonNoGrid
          variant='contained'
          onClick={ () => { downloadData(userHasRole, setDownloadLoading, kitsFilterQuery); } }
          label='Download'
          loading={ downloadLoading }
        />
        <LoadingButtonNoGrid
          loading={ downloadAllLoading }
          variant='contained'
          label='Download Last'
          onClick={ () => {
            if (selectedMonths === 'All') {
              downloadData(userHasRole, setDownloadAllLoading, allKitsFilterQuery);
            } else {
              downloadData(userHasRole, setDownloadAllLoading, { ...allKitsFilterQuery, _limit: -1, created_at_gte: startDate });
            }
          } }
        />
        <MonthSelector selectedMonths={ selectedMonths } handleChange={ handleChange } />
        {
          (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)
            || userHasRole(roles.NUTRITION_GENETICS_USER))
          && (
            <SearchAndFilterButtons
              setFilterQuery={ setFilterQuery }
              filterQuery={ filterQuery }
              reportType='NG'
            />
          )
        }

      </div>
      <AppTable
        columns={ tableColumns }
        data={ kits }
        loading={ kitsLoading || allKitsLoading || loading }
        paginationProps={ pagination }
        refetch={ refetch }
        advancedFiltersProps={
          userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)
          || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST) || userHasRole(roles.NUTRITION_GENETICS_USER)
            ? null
            : {
              properties: [
                {
                  value: 'serial_number_id.serial',
                  label: 'Serial Number',
                  type: filterTypes.FREE_TEXT,
                },
                {
                  value: 'analysis_approval',
                  label: 'Analysis approval',
                  type: filterTypes.CATEGORY,
                  options: [
                    { value: false, label: 'FALSE' },
                    { value: true, label: 'TRUE' },
                  ],
                },
                {
                  value: 'third_party_approval',
                  label: 'Scientific Approval',
                  type: filterTypes.CATEGORY,
                  options: [
                    { value: false, label: 'FALSE' },
                    { value: true, label: 'TRUE' },
                  ],
                },
                {
                  label: 'Registration Status ', value: 'registrationStatus', isRegistration: true, type: filterTypes.FREE_TEXT,
                },
                {
                  value: 'child_id',
                  label: 'Children',
                  type: filterTypes.CATEGORY,
                  options: [
                    { value: false, label: 'True' },
                    { value: true, label: 'False' },
                  ],
                },
              ],
              initialAndFilterOptions: [],
              onQueryChange: setFilterQuery,
            }
        }
        selectionProps={ { setSelected, selected } }
      />
    </div>
  );
};

export default NgReportsTable;
